/*
    같은 Y 축을 공유하는 여러 개의 차트.
    옵션에 따라 1개의 svg 또는 축과 차트를 분리하여 array로 반환
 */
import * as d3 from "d3"

import i18n from "@/i18n"
import {
  ChartMultiple,
  ChartMultipleItem,
  ChartRow,
  Size,
  Margin,
  Rectangle,
} from "@/app/report/common/constructor"
import { CHART_TYPE, VALUE_POSITION_TYPE, VALUE_ORNAMENT_TYPE } from "@/app/report/common/constants"
import {
  getLocaleString,
  getDecimalPointForTick,
  getTicks,
  resolveDecimalPoint,
} from "@/app/report/common/tools"
import { colors } from "@/components/colors"
import { TYPOGRAPHY } from "@/components/typography"
import { convertUnitSquare } from "@/utils/helper"

import "./multiple.scss"

const PLOT_DISTANCE_LIMIT = 10 // 플롯 간격 제한
// const X_AXIS_Y_POSITION_MOVE = 0.5 // X축 위치로 인해 겹쳐보이는 현상 방지 TODO unused 제거
const CHART_WIDTH = 880 // 차트 너비
const CHART_HEIGHT = 175 // 차트 높이

/*
      각 화면 요소들이 사용하는 크기 또는 비율에 대한 정의
    */
export class Space {
  xAxisLabelHeight: number // X 축 라벨을 표시하는데 사용하는 높이
  yAxisLabelWidth: number // Y 축 라벨을 표시하는데 사용하는 폭
  xAxisChartNameWidth: number // 차트 이름을 표시하는데 사용하는 폭(Interval 차트에서 세션 번호를 'S0'와 같은 형식으로 표시하는데 이 때 사용하는 너비)
  betweenXAxisAndLabelHeight: number // X축과 라벨 사이의 간격
  yAxisTickAndSpace: number // Y축 tick 크기 + 라벨과의 간격 (동적 계산)

  betweenRightYAxis = 10 // 오른쪽 Y축 사이의 간격

  marginBetweenItemsRatio = 1 // bar 차트를 그릴 때 X축 아이템 간의 여백 비율(bar 그래프의 크기를 정할 때 사용)
  marginBetweenBarsRatio = 0.16 // bar 차트를 여러개 동시에 표시할 때 각 bar 사이의 간격 비율
  valueLineHeightRatio = 1.3 // 값이 표시되는 라인의 높이 비율(폰트 크기와 연동)
  betweenTableAndChartRatio = 0.8 // 테이블과 차트 사이의 간격(폰트 크기와 연동)
  betweenValueAndChartRatio = 0.1 // 값과 차트 사이의 간격(폰트 크기와 연동)

  constructor({
    xAxisLabelHeight,
    yAxisLabelWidth,
    xAxisChartNameWidth,
    betweenXAxisAndLabelHeight,
    magnification = 1.0,
  }: {
    xAxisLabelHeight: number
    yAxisLabelWidth: number
    xAxisChartNameWidth: number
    betweenXAxisAndLabelHeight: number
    magnification?: number
  }) {
    this.betweenRightYAxis = this.betweenRightYAxis * magnification

    this.xAxisLabelHeight = xAxisLabelHeight * magnification
    this.yAxisLabelWidth = yAxisLabelWidth * magnification
    this.xAxisChartNameWidth = xAxisChartNameWidth * magnification
    this.betweenXAxisAndLabelHeight = betweenXAxisAndLabelHeight * magnification
    this.yAxisTickAndSpace = 12 * magnification
  }

  calculateTickAndSpace(maxDigits: number, magnification: number = 1.0) {
    const baseSpace = 10
    const perDigitSpace = 1
    this.yAxisTickAndSpace = (baseSpace + maxDigits * perDigitSpace) * magnification
  }
}

/*
      차트 라벨에 대한 정의
      interval 차트인 경우 각 세션 이름을 S+'숫자' 형태로 차트 시작 부분에 표시할 때 사용
    */
export class ChartLabel {
  rx: number // radius on the x-axis
  ry: number // radius on the y-axis
  x: number // position x
  y: number // position y
  width: number // width
  height: number // height
  spaceBetweenTickAndLabelOutline: number // x축 tick과 라벨 외곽선 사이의 간격
  spaceBetweenTickAndLabelText: number // x축 tick과 라벨 텍스트 사이의 간격

  constructor({
    rx,
    ry,
    x,
    y,
    width,
    height,
    spaceBetweenTickAndLabelOutline,
    spaceBetweenTickAndLabelText,
  }: {
    rx: number
    ry: number
    x: number
    y: number
    width: number
    height: number
    spaceBetweenTickAndLabelOutline: number
    spaceBetweenTickAndLabelText: number
  }) {
    this.rx = rx
    this.ry = ry
    this.x = x
    this.y = y
    this.width = width
    this.height = height
    this.spaceBetweenTickAndLabelOutline = spaceBetweenTickAndLabelOutline
    this.spaceBetweenTickAndLabelText = spaceBetweenTickAndLabelText
  }
}

export class ChartStyle {
  magnification: number // 확대 배율
  xAxisLabelTextLengthLimit: number // x축 라벨 텍스트 길이 제한

  // Min / Max Size
  arrayWidthOfBar: Array<number> // 바 너비
  widthOfXAxisItem: number // 실제 사용하는 X축 아이템의 너비(차트 공간에 대해 계산하는 과정에서 변경됨. x축 아이템의 갯수에 따라 달라짐)

  // Size and Margin
  size: Size // 차트의 크기
  margin: Margin // 차트 내부 여백
  space: Space // 차트 내부 요소들이 사용하는 공간의 크기나 비율
  sizeCharts: Array<Size> // Y축을 제외한 각 차트들의 크기
  rectAreaCharts: Array<Rectangle> // 각 축 영역과 여백을 제외한 차트 영역
  sizeYAxis: Array<Size> // 각 Y축의 크기
  rectAreaYAxis: Array<Rectangle> // X축 영역과 여백을 제외한 Y축 영역

  // Point 차트 요소에 대한 정의
  circleRadius: number // Point 차트의 반지름
  circleStrokeWidth: number // Point 차트의 테두리 두께
  circleStrokeColor: string // Point 차트의 테두리 색상

  // Color
  colorAxisXLabel: string // X축 라벨 색상
  colorAxisYLabel: string // Y축 라벨 색상

  // Line Style
  gridStrokeWidth: number // 그리드 선 두께
  lineWidth: number // 선 그래프의 두께

  // Flags
  flagShowGridX: boolean // X축 그리드 표시 여부
  flagShowGridY: boolean // Y축 그리드 표시 여부
  flagShowTickStroke: boolean // Tick 선 표시 여부
  flagShowChartLabelOnXAxis: boolean // 차트 이름을 X축 하단에 표시하는지 여부(interval 차트인 경우 각 세션 이름을 S+'숫자' 형태로 차트 시작 부분에 표시할 때 사용)
  flagLongNameOnXAxis: boolean // X축 라벨의 이름이 긴 경우(true인 경우 하단 여백을 늘리고 라벨을 45도 회전하여 표시)

  // Font Style
  fontFamily: string // 폰트
  fontSize: number // 차트 요소들의 기본 폰트 크기
  fontSizeLegend: number // 범례의 폰트 크기
  fontSizeNoData: number // 데이터가 없을 때 표시하는 텍스트의 폰트 크기
  fontSizeValue: number // 값의 폰트 크기
  fontWeight: number // 기본 폰트 굵기
  fontWeightNoData: number // 데이터가 없을 때 표시하는 텍스트의 폰트 두께
  fontWeightChartName: number // 차트 이름의 폰트 굵기(interval 차트에서 세션 이름을 'S0'와 같이 표시할 때 사용)
  fontWeightValue: number // 값의 폰트 두께
  fontColorValue: string // 값의 색상
  fontStrokeWidthValue: number // 값의 테두리 두께

  // chart label
  chartLabel: ChartLabel // 차트 라벨에 대한 정의(interval 차트에서 세션 이름을 'S0'와 같이 표시할 때 사용)

  // ticks
  ticksYAxisMax: number // Y축의 tick 갯수

  // value
  valuePositionType: VALUE_POSITION_TYPE // 값의 위치
  valueOrnamentType: VALUE_ORNAMENT_TYPE // 값의 장식

  fullWidth: boolean
  isPreview: boolean
  alignLeft: boolean
  // 한 섹션이 여러 조각으로 나뉠 때 항목 폭을 맞추기 위한 기준 개수.
  // 0 이면 실제 항목 수를 쓴다(기존 동작).
  referenceXAxisItemCount: number
  /**
   * Y축 눈금값이 차지할 최소 폭.
   * 기본 30 은 화면 폰트(11px) 기준이라, 폰트가 작은 출력용에서는 계산값(15~24px)이 전부
   * 이 하한에 막혀 축마다 빈 공간이 남는다. 출력용은 낮춰서 차트 폭을 되찾는다.
   */
  minYAxisTickValueWidth: number

  constructor({
    width = 1200,
    height = 600,
    flagShowChartLabelOnXAxis = true,
    flagLongNameOnXAxis = false,
    magnification = 1.0,
  }) {
    if (width < 10) {
      width = 10
    }

    if (height < 10) {
      height = 10
    }

    if (magnification < 0.1) {
      magnification = 0.1
    }

    if (magnification > 10.0) {
      magnification = 10.0
    }

    width = width - 1 // 1px 빼는 이유는 브라우저에서 스크롤이 생기지 않도록 하기 위함

    this.magnification = magnification
    this.xAxisLabelTextLengthLimit = 20

    const spaceForXAxisLabel = flagLongNameOnXAxis ? 80 : 40
    const spaceForName = flagLongNameOnXAxis ? 50 : 32

    this.arrayWidthOfBar = [18, 15, 12]

    this.widthOfXAxisItem = 24 * 3 * magnification
    this.size = new Size(width * magnification, height * magnification)
    this.margin = new Margin(
      8 * magnification,
      8 * magnification,
      8 * magnification,
      8 * magnification,
    )
    this.space = new Space({
      xAxisLabelHeight: spaceForXAxisLabel,
      yAxisLabelWidth: 32,
      xAxisChartNameWidth: spaceForName,
      betweenXAxisAndLabelHeight: 12,
      magnification,
    })
    this.sizeCharts = []
    this.rectAreaCharts = []
    this.sizeYAxis = []
    this.rectAreaYAxis = []

    this.circleRadius = 6 * magnification
    this.circleStrokeWidth = 2 * magnification
    this.circleStrokeColor = colors.neutral.white
    this.colorAxisXLabel = colors.neutral.grey["1000"]
    this.colorAxisYLabel = colors.neutral.grey["600"]

    this.gridStrokeWidth = 1 * magnification
    this.lineWidth = 3 * magnification

    this.flagShowGridX = false
    this.flagShowGridY = true
    this.flagShowTickStroke = true
    this.flagShowChartLabelOnXAxis = flagShowChartLabelOnXAxis
    this.flagLongNameOnXAxis = flagLongNameOnXAxis

    this.fontFamily = TYPOGRAPHY.FONT_FAMILY_DEFAULT
    this.fontSize = 11 * magnification
    this.fontSizeLegend = 14 * magnification
    this.fontSizeNoData = 12 * magnification
    this.fontSizeValue = 11 * magnification
    this.fontWeight = 400
    this.fontWeightNoData = 400
    this.fontWeightChartName = 700
    this.fontWeightValue = 600
    this.fontColorValue = colors.neutral.grey["1000"]
    this.fontStrokeWidthValue = 3 * magnification

    this.chartLabel = new ChartLabel({
      rx: 6 * magnification, // radius on the x-axis
      ry: 6 * magnification, // radius on the y-axis
      x: -12 * magnification, // position x
      y: -12 * magnification, // position y
      width: 24 * magnification, // width
      height: 18 * magnification, // height
      spaceBetweenTickAndLabelOutline: 7 * magnification, // x축 tick과 라벨 외곽선 사이의 간격
      spaceBetweenTickAndLabelText: 8 * magnification, // x축 tick과 라벨 텍스트 사이의 간격
    })

    this.ticksYAxisMax = 5

    this.valuePositionType = VALUE_POSITION_TYPE.HIDE_VALUES
    this.valueOrnamentType = VALUE_ORNAMENT_TYPE.NONE

    this.fullWidth = false
    this.isPreview = false
    this.alignLeft = false
    this.referenceXAxisItemCount = 0
    this.minYAxisTickValueWidth = 30
  }
}

// 문자열을 지정된 길이로 줄이기
function stringShortener(strLabel: string, length: number): string {
  if (strLabel.length <= length) {
    return strLabel
  } else {
    return `${strLabel.substring(0, length)}...`
  }
}

// 차트 생성 결과
export interface ResultCreateChart {
  svgCharts: Array<SVGSVGElement | null>
  svgYAxisLeft: SVGSVGElement | null
  svgYAxisRight: SVGSVGElement | null
}

export function createChart({
  chartMultiple,
  style,
  mouseover,
  mousemove,
  mouseout,
  translateKey,
  decimalPointForExportFile = 1,
  t,
}: {
  chartMultiple: ChartMultiple
  style: ChartStyle
  mouseover: (e: MouseEvent, index: number, indexChart: number, chartName: string) => void
  mousemove: (e: MouseEvent) => void
  mouseout: (e: MouseEvent) => void
  translateKey?: (key: string) => string | undefined
  decimalPointForExportFile?: number
  t?: (key: string) => string
}): ResultCreateChart | null {
  if (!chartMultiple) {
    return null
  }

  // X축 아이템의 갯수
  // TODO vue 프로젝트와 갯수가 다르게 나옴
  const countOfXAxisItem = chartMultiple.charts.reduce(
    (acc: number, cur: ChartMultipleItem) => acc + cur.rows.length,
    0,
  )

  // 데이터가 없는 경우
  const flagEmpty = countOfXAxisItem === 0

  if (flagEmpty) {
    style.space.xAxisChartNameWidth = 0
  }

  const axisYWidthForTickValue = new Array<number>()
  let maxDigitCount = 0

  if (flagEmpty) {
    chartMultiple.axisY.forEach(() => {
      axisYWidthForTickValue.push(0)
    })
  } else {
    chartMultiple.axisY.forEach((_, index) => {
      const valueMax =
        chartMultiple.charts.reduce(
          (acc: number, cur: ChartMultipleItem) =>
            Math.max(
              acc,
              cur.rows.reduce(
                (acc: number, cur: ChartRow) => Math.max(acc, cur.data[index] || 0),
                0,
              ),
            ),
          0,
        ) || 1
      const decimalPointForTick = getDecimalPointForTick(
        chartMultiple.axisY[index].decimalPoint,
        valueMax,
        style.ticksYAxisMax,
      )
      const valueString = getLocaleString(Number(valueMax), decimalPointForTick)
      const numbersOnly = valueString.replace(/\D/g, "")

      maxDigitCount = Math.max(maxDigitCount, numbersOnly.length)

      const digitWidth = numbersOnly.length * style.fontSize * 0.7
      const separatorWidth = (valueString.length - numbersOnly.length) * style.fontSize * 0.35
      const calculatedWidth = digitWidth + separatorWidth + 10
      const width = Math.max(calculatedWidth, style.minYAxisTickValueWidth * style.magnification)

      axisYWidthForTickValue.push(width)
    })

    style.space.calculateTickAndSpace(maxDigitCount, style.magnification)
  }

  let rightAxisYBetween = 0
  if (chartMultiple.axisY.length > 1) {
    rightAxisYBetween = style.space.betweenRightYAxis * (chartMultiple.axisY.length - 1)
  }

  const hasData = (indexY: number): boolean => {
    return chartMultiple.charts.some((chartItem) =>
      chartItem.rows.some((row) => {
        const value = row.data[indexY]

        return value != null && !Number.isNaN(value) && value !== 0
      }),
    )
  }

  let countOfBarChart = 0
  const barAndStackedIndices = new Set<number>()

  chartMultiple.axisY.forEach((y, indexY) => {
    if (
      y.flagShow &&
      (y.chartType === CHART_TYPE.BAR || y.chartType === CHART_TYPE.STACKED_BAR) &&
      hasData(indexY)
    ) {
      barAndStackedIndices.add(indexY)
    }
  })

  const stackedGroupsForCount: Map<string, number[]> = new Map()
  chartMultiple.axisY.forEach((y, indexY) => {
    if (y.flagShow && y.chartType === CHART_TYPE.STACKED_BAR && hasData(indexY)) {
      const unit = y.unit || "default"
      if (!stackedGroupsForCount.has(unit)) {
        stackedGroupsForCount.set(unit, [])
      }
      stackedGroupsForCount.get(unit)?.push(indexY)
    }
  })

  const stackedGroupMembers = new Set<number>()
  stackedGroupsForCount.forEach((indices) => {
    if (indices.length > 1) {
      indices.forEach((idx) => stackedGroupMembers.add(idx))
      countOfBarChart += 1
    }
  })

  barAndStackedIndices.forEach((indexY) => {
    if (!stackedGroupMembers.has(indexY)) {
      countOfBarChart += 1
    }
  })

  const indexBarSize = Math.max(Math.min(countOfBarChart - 1, style.arrayWidthOfBar.length - 1), 0)
  const singleBarWidth = style.arrayWidthOfBar[indexBarSize] * style.magnification
  let spaceBetweenItem = style.space.marginBetweenItemsRatio * singleBarWidth
  const spaceBetweenBars = style.space.marginBetweenBarsRatio * singleBarWidth
  let widthOfAllBarAndSpace = singleBarWidth + spaceBetweenItem
  if (countOfBarChart > 1) {
    widthOfAllBarAndSpace += (countOfBarChart - 1) * (singleBarWidth + spaceBetweenBars)
  }
  Object.freeze(widthOfAllBarAndSpace)

  const spaceForYAxisLabel = style.space.yAxisLabelWidth * chartMultiple.axisY.length
  const spaceForYAxisTickValue = axisYWidthForTickValue.reduce(
    (acc: number, cur: number) => acc + cur,
    0,
  )
  const spaceForRightYAxisMargin =
    chartMultiple.axisY.length >= 2 ? rightAxisYBetween * (chartMultiple.axisY.length - 2) : 0
  // 한 섹션이 여러 조각으로 나뉠 때, 마지막 조각처럼 항목이 적게 남은 차트도 앞 조각과
  // 같은 항목 폭을 갖도록 기준 개수로 계산한다. 실제 개수로 계산하면 남는 폭을 적은
  // 항목들에 몰아줘서 3개짜리 조각의 막대가 14개짜리보다 몇 배 넓어졌다.
  const countForWidth = style.referenceXAxisItemCount || countOfXAxisItem
  const spaceForXAxisChartItems = widthOfAllBarAndSpace * countForWidth
  const spaceForYAxisTickAndLabel = style.space.yAxisTickAndSpace * chartMultiple.axisY.length
  const spaceForChartName = style.space.xAxisChartNameWidth * chartMultiple.charts.length

  const widthRequired = style.alignLeft
    ? style.size.width * style.magnification
    : style.margin.left +
      style.margin.right +
      spaceForYAxisLabel +
      spaceForRightYAxisMargin +
      spaceForYAxisTickValue +
      spaceForXAxisChartItems +
      spaceForYAxisTickAndLabel +
      spaceForChartName

  let widthOfXAxisItem = widthOfAllBarAndSpace
  if (countOfXAxisItem === 0) {
    widthOfXAxisItem =
      style.size.width -
      style.margin.left -
      style.margin.right -
      spaceForYAxisLabel -
      rightAxisYBetween -
      spaceForYAxisTickValue -
      spaceForYAxisTickAndLabel
  } else if (widthRequired < style.size.width) {
    const widthRemainForEachItem = (style.size.width - widthRequired) / countForWidth
    widthOfXAxisItem += widthRemainForEachItem
    spaceBetweenItem += widthRemainForEachItem
  }

  style.widthOfXAxisItem = widthOfXAxisItem
  Object.freeze(widthOfXAxisItem)
  Object.freeze(style.widthOfXAxisItem)
  Object.freeze(spaceBetweenItem)

  const axisYLeft = new Array(chartMultiple.axisY.length).fill(0)
  const axisYWidth = new Array(chartMultiple.axisY.length).fill(0)

  const stackedGroupsForPosition: Map<string, number[]> = new Map()
  chartMultiple.axisY.forEach((y, indexY) => {
    if (y.flagShow && y.chartType === CHART_TYPE.STACKED_BAR) {
      const unit = y.unit || "default"
      if (!stackedGroupsForPosition.has(unit)) {
        stackedGroupsForPosition.set(unit, [])
      }
      stackedGroupsForPosition.get(unit)?.push(indexY)
    }
  })

  const stackedIndicesSet = new Set<number>()
  const stackedGroupRepresentatives: number[] = []
  stackedGroupsForPosition.forEach((indices) => {
    if (indices.length > 1) {
      indices.forEach((idx) => stackedIndicesSet.add(idx))
      stackedGroupRepresentatives.push(indices[0])
    }
  })

  const visualColumnIndices: number[] = []
  chartMultiple.axisY.forEach((y, indexY) => {
    if (y.flagShow && (y.chartType === CHART_TYPE.BAR || y.chartType === CHART_TYPE.STACKED_BAR)) {
      if (stackedIndicesSet.has(indexY)) {
        if (stackedGroupRepresentatives.includes(indexY)) {
          visualColumnIndices.push(indexY)
        }
      } else {
        visualColumnIndices.push(indexY)
      }
    }
  })

  if (visualColumnIndices.length === 2) {
    const firstBarStart = widthOfXAxisItem / 2 - singleBarWidth - spaceBetweenBars / 2

    let stackedColumnIndex = -1
    let nonStackedColumnIndex = -1

    visualColumnIndices.forEach((indexY, columnIndex) => {
      let isPartOfStackedGroup = false
      stackedGroupsForPosition.forEach((indices) => {
        if (indices.includes(indexY) && indices.length > 1) {
          isPartOfStackedGroup = true
        }
      })

      if (isPartOfStackedGroup) {
        stackedColumnIndex = columnIndex
      } else {
        nonStackedColumnIndex = columnIndex
      }
    })

    let reorderedVisualColumnIndices = [...visualColumnIndices]
    if (stackedColumnIndex === 0 && nonStackedColumnIndex === 1) {
      reorderedVisualColumnIndices = [visualColumnIndices[1], visualColumnIndices[0]]
    }

    reorderedVisualColumnIndices.forEach((indexY, columnIndex) => {
      const position =
        columnIndex === 0 ? firstBarStart : firstBarStart + singleBarWidth + spaceBetweenBars

      let assigned = false
      stackedGroupsForPosition.forEach((indices) => {
        if (indices.includes(indexY) && indices.length > 1) {
          indices.forEach((idx) => {
            axisYLeft[idx] = position
            axisYWidth[idx] = singleBarWidth
          })
          assigned = true
        }
      })

      if (!assigned) {
        axisYLeft[indexY] = position
        axisYWidth[indexY] = singleBarWidth
      }
    })
  } else {
    const totalWidth =
      visualColumnIndices.length * singleBarWidth +
      (visualColumnIndices.length - 1) * spaceBetweenBars
    const startOffset = widthOfXAxisItem / 2 - totalWidth / 2

    let left = startOffset
    visualColumnIndices.forEach((indexY) => {
      let assigned = false
      stackedGroupsForPosition.forEach((indices) => {
        if (indices.includes(indexY) && indices.length > 1) {
          indices.forEach((idx) => {
            axisYLeft[idx] = left
            axisYWidth[idx] = singleBarWidth
          })
          assigned = true
        }
      })

      if (!assigned) {
        axisYLeft[indexY] = left
        axisYWidth[indexY] = singleBarWidth
      }

      left = left + singleBarWidth + spaceBetweenBars
    })
  }

  let spaceForValuesHeight = 0
  let spaceForValuesTop = 0
  if (!flagEmpty) {
    switch (style.valuePositionType) {
      case VALUE_POSITION_TYPE.ABOVE:
      case VALUE_POSITION_TYPE.BELOW:
      case VALUE_POSITION_TYPE.IN_CHART_IN_ORDER:
        spaceForValuesHeight =
          style.fontSize * style.space.valueLineHeightRatio * chartMultiple.axisY.length +
          style.space.betweenTableAndChartRatio * style.fontSize
        break

      // 막대 안(IN_BAR)도 선·점·에어리어 값은 점 위에 쓰므로 맨 위 값이 잘리지 않게 한 줄을 비운다 (2026-10-01 보고)
      case VALUE_POSITION_TYPE.IN_CHART_EACH_POSITION:
      case VALUE_POSITION_TYPE.IN_CHART_EACH_POSITION_MIN_MAX:
      case VALUE_POSITION_TYPE.IN_BAR:
        spaceForValuesHeight =
          style.fontSize * style.space.valueLineHeightRatio +
          style.space.betweenTableAndChartRatio * style.fontSize
        break
    }

    switch (style.valuePositionType) {
      case VALUE_POSITION_TYPE.ABOVE:
      case VALUE_POSITION_TYPE.IN_CHART_IN_ORDER:
      case VALUE_POSITION_TYPE.IN_CHART_EACH_POSITION:
      case VALUE_POSITION_TYPE.IN_CHART_EACH_POSITION_MIN_MAX:
      case VALUE_POSITION_TYPE.IN_BAR:
        spaceForValuesTop = spaceForValuesHeight
        break
    }
  }

  const stackedGroupsForAxisSizing: Map<string, number[]> = new Map()
  chartMultiple.axisY.forEach((y, indexY) => {
    if (y.chartType === CHART_TYPE.STACKED_BAR) {
      const unit = y.unit || "default"
      if (!stackedGroupsForAxisSizing.has(unit)) {
        stackedGroupsForAxisSizing.set(unit, [])
      }
      stackedGroupsForAxisSizing.get(unit)?.push(indexY)
    }
  })

  let stackedGroupIndicesForAxisSizing: number[] = []
  stackedGroupsForAxisSizing.forEach((indices) => {
    if (indices.length > 1) {
      stackedGroupIndicesForAxisSizing = indices
    }
  })

  const shouldHideRightAxisForSizing = stackedGroupIndicesForAxisSizing.length === 3

  style.sizeYAxis.push(
    new Size(
      style.margin.left +
        style.space.yAxisLabelWidth +
        axisYWidthForTickValue[0] +
        style.space.yAxisTickAndSpace,
      style.size.height,
    ),
  )
  const axisLeftWidth = style.sizeYAxis[0].width

  style.rectAreaYAxis.push(
    new Rectangle({
      x: style.margin.left,
      y: style.margin.top + spaceForValuesTop,
      width: style.sizeYAxis[0].width - style.margin.left,
      height: style.size.height - style.margin.top - style.margin.bottom - spaceForValuesHeight,
    }),
  )

  if (chartMultiple.axisY.length > 1) {
    const has2StackedBars = stackedGroupIndicesForAxisSizing.length === 2

    let size: Size
    if (shouldHideRightAxisForSizing) {
      size = new Size(style.margin.right, style.size.height)
    } else if (has2StackedBars) {
      size = new Size(
        style.space.yAxisLabelWidth +
          style.space.yAxisTickAndSpace +
          axisYWidthForTickValue[stackedGroupIndicesForAxisSizing[0]] +
          style.margin.right,
        style.size.height,
      )
    } else {
      size = new Size(
        (style.space.yAxisLabelWidth +
          style.space.betweenRightYAxis +
          style.space.yAxisTickAndSpace) *
          (chartMultiple.axisY.length - 1) +
          axisYWidthForTickValue.slice(1).reduce((acc: number, cur: number) => acc + cur, 0) +
          style.margin.right,
        style.size.height,
      )
    }

    style.sizeYAxis.push(size)
    style.rectAreaYAxis.push(
      new Rectangle({
        x: 0,
        y: style.margin.top + spaceForValuesTop,
        width: shouldHideRightAxisForSizing ? 0 : size.width - style.margin.right,
        height:
          size.height -
          style.margin.top -
          style.margin.bottom -
          style.space.xAxisLabelHeight -
          spaceForValuesHeight,
      }),
    )
  }

  if (flagEmpty) {
    const sizeForEachChart = new Size(
      style.size.width -
        style.margin.left -
        style.margin.right -
        style.space.yAxisLabelWidth * chartMultiple.axisY.length -
        style.space.betweenRightYAxis * (chartMultiple.axisY.length - 1) +
        style.space.yAxisTickAndSpace * chartMultiple.axisY.length,
      style.size.height,
    )
    style.sizeCharts.push(sizeForEachChart)
    style.rectAreaCharts.push(
      new Rectangle({
        x: 0,
        y: style.margin.top,
        width: sizeForEachChart.width,
        height:
          sizeForEachChart.height -
          style.margin.top -
          style.margin.bottom -
          style.space.xAxisLabelHeight,
      }),
    )
  } else {
    chartMultiple.charts.forEach((chart) => {
      const size = new Size(
        chart.rows.length * widthOfXAxisItem + (style.space.xAxisChartNameWidth / 4) * 3,
        style.size.height,
      )
      style.sizeCharts.push(size)
      style.rectAreaCharts.push(
        new Rectangle({
          x: style.space.xAxisChartNameWidth / 2,
          y: style.margin.top + spaceForValuesTop,
          width: size.width - style.space.xAxisChartNameWidth,
          height:
            size.height -
            style.margin.top -
            style.margin.bottom -
            style.space.xAxisLabelHeight -
            spaceForValuesHeight,
        }),
      )
    })
  }

  const svgYAxis = Array<d3.Selection<SVGSVGElement, undefined, null, undefined>>()
  style.sizeYAxis.forEach((y) => {
    svgYAxis.push(d3.create("svg").attr("viewBox", `0 0 ${y.width} ${y.height}`))
  })

  const svgCharts = Array<d3.Selection<SVGSVGElement, undefined, null, undefined>>()
  style.sizeCharts.forEach((chart) => {
    const chartWidth = style.fullWidth
      ? Math.max(style.size.width, chart.width, CHART_WIDTH)
      : chart.width
    const chartHeight = style.isPreview ? chart.height + style.space.xAxisLabelHeight : chart.height
    svgCharts.push(
      d3
        .create("svg")
        .attr("viewBox", `0 0 ${chartWidth} ${chartHeight}`)
        .style("overflow", "visible"),
    )
  })

  const axisYDomain: Array<d3.ScaleLinear<number, number, never>> = []
  const axisYTickDecimalPoint = Array<number>()
  const axisYTicks = Array<number>()

  const stackedGroups: Map<string, number[]> = new Map()
  chartMultiple.axisY.forEach((y, indexY) => {
    if (y.chartType === CHART_TYPE.STACKED_BAR) {
      const unit = y.unit || "default"
      if (!stackedGroups.has(unit)) {
        stackedGroups.set(unit, [])
      }
      stackedGroups.get(unit)?.push(indexY)
    }
  })

  const actualStackedIndices = new Set<number>()
  stackedGroups.forEach((indices) => {
    if (indices.length > 1) {
      indices.forEach((idx) => actualStackedIndices.add(idx))
    }
  })

  chartMultiple.axisY.forEach((_, index) => {
    let valueMax: number

    if (actualStackedIndices.has(index)) {
      let groupIndices: number[] = []
      stackedGroups.forEach((indices) => {
        if (indices.includes(index)) {
          groupIndices = indices
        }
      })

      valueMax = groupIndices.reduce((sum, idx) => {
        const axis = chartMultiple.axisY[idx]

        if (axis.manualScaleActive && axis.manualScaleValue != null) {
          return sum + axis.manualScaleValue
        } else {
          const maxForMetric =
            chartMultiple.charts.reduce(
              (acc: number, cur: ChartMultipleItem) =>
                Math.max(
                  acc,
                  cur.rows.reduce(
                    (acc: number, cur: ChartRow) => Math.max(acc, cur.data[idx] || 0),
                    0,
                  ),
                ),
              0,
            ) || 0

          return sum + maxForMetric
        }
      }, 0)
    } else {
      const yAxisConfig = chartMultiple.axisY[index]

      if (yAxisConfig.manualScaleActive && yAxisConfig.manualScaleValue != null) {
        valueMax = yAxisConfig.manualScaleValue
      } else {
        valueMax =
          chartMultiple.charts.reduce(
            (acc: number, cur: ChartMultipleItem) =>
              Math.max(
                acc,
                cur.rows.reduce(
                  (acc: number, cur: ChartRow) => Math.max(acc, cur.data[index] || 0),
                  0,
                ),
              ),
            0,
          ) || 1
      }
    }

    valueMax = valueMax || 1

    const metricDecimalPoint = getDecimalPointForTick(
      chartMultiple.axisY[index].decimalPoint,
      valueMax,
      style.ticksYAxisMax,
    )
    axisYTickDecimalPoint.push(metricDecimalPoint)
    axisYDomain.push(
      d3
        .scaleLinear()
        .domain([0, valueMax])
        .nice()
        .range([style.rectAreaCharts[0].bottom, style.rectAreaCharts[0].top]),
    )
    axisYTicks.push(
      getTicks(chartMultiple.axisY[index].decimalPoint, valueMax, style.ticksYAxisMax),
    )
  })

  if (flagEmpty) {
    const axisXDomain = d3
      .scaleBand()
      .domain([])
      .range([style.rectAreaCharts[0].left, style.rectAreaCharts[0].right])
      .padding(0.0)

    const axisXTicks = d3.axisBottom(axisXDomain).tickSizeOuter(0)

    svgCharts[0]
      .append("g")
      .attr("transform", `translate(0, ${style.rectAreaCharts[0].bottom})`)
      .call(axisXTicks)
      .call((g) => {
        if (!style.flagShowTickStroke) {
          g.selectAll(".tick line").remove()
        }
      })

    svgCharts[0]
      .append("text")
      .attr("text-anchor", "middle")
      .attr("x", style.rectAreaCharts[0].left + style.rectAreaCharts[0].width / 2)
      .attr("y", style.rectAreaCharts[0].top + style.rectAreaCharts[0].height / 2)
      .style("fill", style.colorAxisYLabel)
      .style("font-family", style.fontFamily)
      .style("font-weight", style.fontWeightNoData)
      .style("font-size", style.fontSizeNoData)
      .text(i18n.t("messages.noDataAvailable").toString())
  }

  chartMultiple.charts.forEach((chart, indexChart) => {
    const indexArrayXDomain = chart.rows.map((_, index) => index.toString())

    const axisXDomain = d3
      .scaleBand()
      .domain(indexArrayXDomain)
      .range([style.rectAreaCharts[indexChart].left, style.rectAreaCharts[indexChart].right])
      .padding(0.0)

    const axisXTicks = d3.axisBottom(axisXDomain).tickSizeOuter(0)

    const axisXTickGroup = svgCharts[indexChart]
      .append("g")
      .attr("transform", `translate(0, ${style.rectAreaCharts[indexChart].bottom})`)
      .attr("stroke-width", style.gridStrokeWidth)
      .call(axisXTicks)
      .call((g) => {
        if (!style.flagShowTickStroke) {
          g.selectAll(".tick line").remove()
        }
      })

    if (style.flagLongNameOnXAxis) {
      axisXTickGroup
        .call(
          axisXTicks.tickFormat((d: string) =>
            stringShortener(chart.rows[parseInt(d)].name, style.xAxisLabelTextLengthLimit),
          ),
        )
        .selectAll("text")
        .style("text-anchor", "end")
        .attr("dx", `${-0.8}em`)
        .attr("dy", `${0.15}em`)
        .attr("transform", "rotate(-45)")
        .style("font-family", style.fontFamily)
        .style("font-weight", style.fontWeight)
        .style("font-size", style.fontSize)
    } else {
      axisXTickGroup
        .call(axisXTicks.tickFormat((d: string) => `${chart.rows[parseInt(d)].name}`))
        .selectAll(".tick text")
        .style("text-anchor", "center")
        .style("font-family", style.fontFamily)
        .style("font-weight", style.fontWeight)
        .style("font-size", style.fontSize)
        .attr("y", style.space.betweenXAxisAndLabelHeight)
    }

    const chartNameLeft = 5

    if (style.space.xAxisChartNameWidth > 0) {
      if (style.flagShowChartLabelOnXAxis) {
        svgCharts[indexChart]
          .append("svg:rect")
          .attr("rx", style.chartLabel.rx)
          .attr("ry", style.chartLabel.ry)
          .attr("x", style.chartLabel.x)
          .attr("y", style.chartLabel.y)
          .attr("width", style.chartLabel.width)
          .attr("height", style.chartLabel.height)
          .attr(
            "transform",
            `translate(${style.space.xAxisChartNameWidth / 2 - chartNameLeft}, ${
              style.rectAreaCharts[indexChart].bottom +
              style.space.betweenXAxisAndLabelHeight +
              style.chartLabel.spaceBetweenTickAndLabelOutline
            })`,
          )
          .style("fill", colors.neutral.alpha["black-08"])

        svgCharts[indexChart]
          .append("text")
          .attr("text-anchor", "middle")
          .attr(
            "transform",
            `translate(${style.space.xAxisChartNameWidth / 2 - chartNameLeft}, ${
              style.rectAreaCharts[indexChart].bottom +
              style.space.betweenXAxisAndLabelHeight +
              style.chartLabel.spaceBetweenTickAndLabelText
            })`,
          )
          .style("fill", style.colorAxisXLabel)
          .style("font-family", style.fontFamily)
          .style("font-size", style.fontSize)
          .style("font-weight", style.fontWeightChartName)
          .style("outline", "single 1px black")
          .text(`${chart.nickname ? chart.nickname : chart.name}`)
      }
    }

    if (!flagEmpty && style.flagShowGridX && axisXDomain) {
      svgCharts[indexChart]
        .selectAll("xGrid")
        .data(axisXDomain.domain())
        .join("line")
        .attr("x1", (d) => axisXDomain(d) || 0)
        .attr("x2", (d) => axisXDomain(d) || 0)
        .attr("y1", style.rectAreaCharts[indexChart].top)
        .attr("y2", style.rectAreaCharts[indexChart].bottom)
        .attr("stroke", colors.neutral.grey["300"])
        .attr("stroke-width", style.gridStrokeWidth)

      svgCharts[indexChart]
        .append("line")
        .attr("x1", style.rectAreaCharts[indexChart].right - 1)
        .attr("x2", style.rectAreaCharts[indexChart].right - 1)
        .attr("y1", style.rectAreaCharts[indexChart].top)
        .attr("y2", style.rectAreaCharts[indexChart].bottom)
        .attr("stroke", colors.neutral.grey["300"])
        .attr("stroke-width", style.gridStrokeWidth)
    }

    if (!flagEmpty) {
      if (style.flagShowGridY && chartMultiple.axisY && chartMultiple.axisY.length > 0) {
        const svgGrid = svgCharts[indexChart]
          .selectAll("yGrid")
          .data(axisYDomain[0].ticks())
          .join("line")

        svgGrid
          .attr("x1", 0)
          .attr(
            "x2",
            Math.max(
              style.fullWidth ? style.size.width : 1,
              style.rectAreaCharts[indexChart].right + style.space.xAxisChartNameWidth / 2,
            ),
          )
          .attr("y1", (d) => axisYDomain[0](d))
          .attr("y2", (d) => axisYDomain[0](d))
          .attr("stroke", colors.neutral.grey["300"])
          .attr("stroke-width", style.gridStrokeWidth)
      }
    }

    if (!flagEmpty) {
      chartMultiple.axisY.forEach((y, indexY) => {
        if (y.flagShow && y.chartType === CHART_TYPE.AREA) {
          const values = chart.rows.map((d) => d.data[indexY])
          svgCharts[indexChart]
            .append("path")
            .datum(values)
            .attr("fill", y.color)
            .attr("fill-opacity", 0.3)
            .attr("stroke", "none")
            .attr(
              "d",
              d3
                .area<number | null>()
                .x(
                  (_: number | null, index: number) =>
                    (axisXDomain(index.toString()) || 0) + widthOfXAxisItem / 2,
                )
                .y0(style.rectAreaCharts[indexChart].bottom)
                .y1((value: number | null) =>
                  Number.isNaN(value) ? 0 : axisYDomain[indexY](value || 0),
                ),
            )

          svgCharts[indexChart]
            .append("path")
            .datum(values)
            .attr("fill", "none")
            .attr("stroke", y.color)
            .attr("stroke-width", style.lineWidth)
            .attr(
              "d",
              d3
                .line<number | null>()
                .x(
                  (_: number | null, index: number) =>
                    (axisXDomain(index.toString()) || 0) + widthOfXAxisItem / 2,
                )
                .y((value: number | null) =>
                  Number.isNaN(value) ? 0 : axisYDomain[indexY](value || 0),
                ),
            )
        }
      })

      const unitGroups: Map<string, number[]> = new Map()
      const stackedUnitGroups: Map<string, number[]> = new Map()

      chartMultiple.axisY.forEach((y, indexY) => {
        if (y.flagShow && y.chartType === CHART_TYPE.STACKED_BAR) {
          const unit = y.unit || "default"
          if (!stackedUnitGroups.has(unit)) {
            stackedUnitGroups.set(unit, [])
          }
          stackedUnitGroups.get(unit)?.push(indexY)
        } else if (y.flagShow && y.chartType === CHART_TYPE.BAR) {
          const uniqueKey = `bar_${indexY}`
          unitGroups.set(uniqueKey, [indexY])
        }
      })

      stackedUnitGroups.forEach((indices, unit) => {
        if (indices.length > 1) {
          unitGroups.set(`stacked_${unit}`, indices)
        } else {
          unitGroups.set(`bar_${indices[0]}`, indices)
        }
      })

      unitGroups.forEach((indicesInGroup) => {
        const shouldStack =
          indicesInGroup.length > 1 &&
          indicesInGroup.every(
            (idx) => chartMultiple.axisY[idx].chartType === CHART_TYPE.STACKED_BAR,
          )

        if (shouldStack) {
          const stackXPosition =
            axisYLeft[indicesInGroup[0]] || widthOfXAxisItem / 2 - singleBarWidth / 2
          const sharedAxisDomain = axisYDomain[indicesInGroup[0]]

          chart.rows.forEach((_, rowIndex) => {
            let cumulativeValue = 0

            indicesInGroup.forEach((indexY) => {
              const value = chart.rows[rowIndex].data[indexY] || 0
              const y = chartMultiple.axisY[indexY]

              if (!Number.isNaN(value) && value !== null && value > 0) {
                const barBottomValue = cumulativeValue
                const barTopValue = cumulativeValue + value

                cumulativeValue = barTopValue

                const barY = sharedAxisDomain(barTopValue)
                const barHeight = sharedAxisDomain(barBottomValue) - sharedAxisDomain(barTopValue)

                const svgBar = svgCharts[indexChart].append("g").attr("fill", y.color)

                svgBar
                  .append("rect")
                  .attr("x", (axisXDomain(rowIndex.toString()) || 0) + stackXPosition)
                  .attr("y", barY)
                  .attr("height", barHeight)
                  .attr("width", singleBarWidth)
                  .attr("stroke-width", style.lineWidth)
              }
            })
          })
        } else {
          indicesInGroup.forEach((indexY) => {
            const y = chartMultiple.axisY[indexY]
            if (
              y.flagShow &&
              (y.chartType === CHART_TYPE.BAR || y.chartType === CHART_TYPE.STACKED_BAR)
            ) {
              const values = chart.rows.map((d) => d.data[indexY])
              const svgBar = svgCharts[indexChart].append("g").attr("fill", y.color)

              svgBar
                .selectAll("rect")
                .data(values)
                .join("rect")
                .attr(
                  "x",
                  (_: number | null, index: number) =>
                    (axisXDomain(index.toString()) || 0) +
                    (axisYLeft[indexY] || spaceBetweenItem / 2),
                )
                .attr("y", (value: number | null) =>
                  Number.isNaN(value) ? 0 : axisYDomain[indexY](value || 0),
                )
                .attr("height", (value: number | null) =>
                  Number.isNaN(value)
                    ? 0
                    : axisYDomain[indexY](0) - axisYDomain[indexY](value || 0),
                )
                .attr("width", axisYWidth[indexY] || singleBarWidth)
                .attr("stroke-width", style.lineWidth)
            }
          })
        }
      })

      chartMultiple.axisY.forEach((y, indexY) => {
        if (y.flagShow && y.chartType === CHART_TYPE.LINE) {
          const values = chart.rows.map((d) => d.data[indexY])
          const svgLine = svgCharts[indexChart].append("path")

          svgLine
            .datum(values)
            .attr("fill", "none")
            .attr("stroke", y.color)
            .attr("stroke-width", style.lineWidth)
            .attr(
              "d",
              d3
                .line<number | null>()
                .x((value: number | null, index: number) =>
                  Number.isNaN(value)
                    ? 0
                    : (axisXDomain(index.toString()) || 0) + widthOfXAxisItem / 2,
                )
                .y((value: number | null) =>
                  Number.isNaN(value) ? 0 : axisYDomain[indexY](value || 0),
                ),
            )
        }
      })

      const scatterCoordinatesArr: number[][][] = []
      chartMultiple.axisY.forEach((y, indexY) => {
        if (
          y.flagShow &&
          (y.chartType === CHART_TYPE.POINT ||
            (y.chartType === CHART_TYPE.LINE && chart.rows.length === 1) ||
            (y.chartType === CHART_TYPE.AREA && chart.rows.length === 1))
        ) {
          const coordinates: number[][] = []
          chart.rows.forEach((r, index) => {
            if (!Number.isNaN(r.data[indexY])) {
              const value = r.data[indexY] || 0

              coordinates.push([
                (axisXDomain(index.toString()) || 0) + widthOfXAxisItem / 2,
                axisYDomain[indexY](value) || 0,
              ])
            }
          })

          scatterCoordinatesArr.push(coordinates)
        }
      })

      if (scatterCoordinatesArr.length) {
        const { length } = scatterCoordinatesArr[0]
        const graphCount = scatterCoordinatesArr.length

        if (graphCount > 1 && scatterCoordinatesArr[1].length && scatterCoordinatesArr[0].length) {
          for (let iterator = 0; iterator < length; iterator++) {
            const firstCoordinate = scatterCoordinatesArr[0][iterator]
            const secondaryCoordinate = scatterCoordinatesArr[1][iterator]
            const [x1] = firstCoordinate
            const [x2] = secondaryCoordinate

            if (
              getDistanceBetweenCoordinates(firstCoordinate, secondaryCoordinate) <=
              PLOT_DISTANCE_LIMIT
            ) {
              firstCoordinate[0] = x1 - 5
              secondaryCoordinate[0] = x2 + 5
            }

            if (graphCount > 2 && scatterCoordinatesArr[2].length) {
              const thirdCoordinate = scatterCoordinatesArr[2][iterator]
              const [x3] = thirdCoordinate

              if (
                getDistanceBetweenCoordinates(secondaryCoordinate, thirdCoordinate) <=
                PLOT_DISTANCE_LIMIT
              ) {
                const x2ChangeVal = secondaryCoordinate[0] === x2 ? -5 : 0

                secondaryCoordinate[0] = x2 + x2ChangeVal
                thirdCoordinate[0] = x3 + 5
              }

              if (
                getDistanceBetweenCoordinates(firstCoordinate, thirdCoordinate) <=
                PLOT_DISTANCE_LIMIT
              ) {
                firstCoordinate[0] = x1 - 5
                thirdCoordinate[0] = x3 + 5
              }
            }
          }
        }

        chartMultiple.axisY.forEach((y, indexY) => {
          if (
            y.flagShow &&
            (y.chartType === CHART_TYPE.POINT ||
              (y.chartType === CHART_TYPE.LINE && chart.rows.length === 1) ||
              (y.chartType === CHART_TYPE.AREA && chart.rows.length === 1))
          ) {
            const coordinatesArr = scatterCoordinatesArr.shift() as number[][]

            chart.rows.forEach((r, index) => {
              if (!Number.isNaN(r.data[indexY])) {
                svgCharts[indexChart]
                  .append("circle")
                  .attr("cx", coordinatesArr[index][0])
                  .attr("cy", coordinatesArr[index][1])
                  .attr("r", style.circleRadius)
                  .attr("fill", y.color)
                  .style("stroke", style.circleStrokeColor)
                  .style("stroke-width", style.circleStrokeWidth)

                // if (style.isPreview) {
                //   svgCharts[indexChart]
                //     .selectAll("circle")
                //     .attr("transform", `translate(${axisLeftWidth}, 0)`)
                // }
              }
            })
          }
        })
      }

      const indexArray = chart.rows.map((_, index) => index)
      chartMultiple.axisY.forEach((y) => {
        svgCharts[indexChart]
          .append("g")
          .attr("fill", y.color)
          .attr("fill-opacity", 0)
          .selectAll("rect")
          .data(indexArray)
          .join("rect")
          .style("mix-blend-mode", "multiply")
          .attr("x", (index: number) => axisXDomain(index.toString()) || 0)
          .attr("y", () => style.rectAreaCharts[indexChart].top)
          .attr("height", style.rectAreaCharts[indexChart].height)
          .attr("width", widthOfXAxisItem + 1)
          .on("mouseover", (e: MouseEvent, index: number) =>
            mouseover(e, index, indexChart, chart.name),
          )
          .on("mousemove", mousemove)
          .on("mouseout", mouseout)
      })

      switch (style.valuePositionType) {
        case VALUE_POSITION_TYPE.ABOVE:
          {
            // 모아서 보기는 표다 — 누적 막대도 시리즈마다 자기 줄에 자기 값을 쓴다 (합만 쓰면 나머지 줄이 빈다, 2026-10-01 보고)
            chartMultiple.axisY.forEach((y, indexY) => {
              if (y.flagShow) {
                chart.rows.forEach((r, index) => {
                  if (!Number.isNaN(r.data[indexY])) {
                    const value = r.data[indexY] || 0
                    const px = (axisXDomain(index.toString()) || 0) + widthOfXAxisItem / 2
                    const py =
                      style.rectAreaCharts[indexChart].top -
                      (chartMultiple.axisY.length - indexY) *
                        style.fontSize *
                        style.space.valueLineHeightRatio -
                      style.fontSize * style.space.betweenValueAndChartRatio

                    addValueText(
                      svgCharts[indexChart],
                      px,
                      py,
                      value,
                      y.color,
                      style,
                      decimalPointForExportFile,
                      axisLeftWidth,
                      y.unit,
                    )
                  }
                })
              }
            })
          }
          break

        case VALUE_POSITION_TYPE.BELOW:
          {
            chartMultiple.axisY.forEach((y, indexY) => {
              if (y.flagShow) {
                chart.rows.forEach((r, index) => {
                  if (!Number.isNaN(r.data[indexY])) {
                    const value = r.data[indexY] || 0
                    const px = (axisXDomain(index.toString()) || 0) + widthOfXAxisItem / 2
                    const py =
                      style.rectAreaCharts[indexChart].bottom +
                      style.space.xAxisLabelHeight +
                      indexY * style.fontSize * style.space.valueLineHeightRatio +
                      style.fontSize * style.space.betweenValueAndChartRatio

                    addValueText(
                      svgCharts[indexChart],
                      px,
                      py,
                      value,
                      y.color,
                      style,
                      decimalPointForExportFile,
                      axisLeftWidth,
                      y.unit,
                    )
                  }
                })
              }
            })
          }
          break

        case VALUE_POSITION_TYPE.IN_CHART_IN_ORDER:
          {
            const arraySmallestTop = new Array<number>(chart.rows.length).fill(0)
            chartMultiple.axisY.forEach((y, indexY) => {
              if (y.flagShow) {
                chart.rows.forEach((r, index) => {
                  if (!Number.isNaN(r.data[indexY])) {
                    const top = axisYDomain[indexY](r.data[indexY] || 0)
                    if (indexY === 0) {
                      arraySmallestTop[index] = top
                    } else if (arraySmallestTop[index] > top) {
                      arraySmallestTop[index] = top
                    }
                  }
                })
              }
            })

            chartMultiple.axisY.forEach((y, indexY) => {
              if (y.flagShow) {
                chart.rows.forEach((r, index) => {
                  if (!Number.isNaN(r.data[indexY])) {
                    const value = r.data[indexY] || 0
                    const px = (axisXDomain(index.toString()) || 0) + widthOfXAxisItem / 2
                    const py =
                      arraySmallestTop[index] -
                      (chartMultiple.axisY.length - indexY) *
                        style.fontSize *
                        style.space.valueLineHeightRatio -
                      style.fontSize * style.space.betweenValueAndChartRatio

                    addValueText(
                      svgCharts[indexChart],
                      px,
                      py,
                      value,
                      y.color,
                      style,
                      decimalPointForExportFile,
                      axisLeftWidth,
                      y.unit,
                    )
                  }
                })
              }
            })
          }
          break

        case VALUE_POSITION_TYPE.IN_CHART_EACH_POSITION:
          {
            const processedStackedRows = new Set<string>()

            // 누적 조각 안에 값을 쓸 수 있는 최소 높이
            const minInsideHeight = style.fontSizeValue * 1.8
            // 값끼리(막대·선·누적 합) 겹치지 않게 — 이미 놓인 값 상자와 겹치면 한 줄 위로 올린다 (2026-10-01 요청)
            const placedBarLabels: Array<{ x: number; y: number; w: number }> = []
            const avoidOverlap = (px: number, py: number, value: number) => {
              const w = (String(Math.round(Math.abs(value))).length + 3) * style.fontSizeValue * 0.6
              let adjusted = py
              for (let guard = 0; guard < 6; guard++) {
                const hit = placedBarLabels.some(
                  (p) =>
                    Math.abs(p.x - px) < (p.w + w) / 2 &&
                    Math.abs(p.y - adjusted) < style.fontSizeValue,
                )
                if (!hit) {
                  break
                }
                adjusted -= style.fontSizeValue * 1.1
              }
              placedBarLabels.push({ x: px, y: adjusted, w })

              return adjusted
            }

            chartMultiple.axisY.forEach((y, indexY) => {
              if (y.flagShow) {
                chart.rows.forEach((r, rowIndex) => {
                  if (!Number.isNaN(r.data[indexY])) {
                    const value = r.data[indexY] || 0
                    let px = axisXDomain(rowIndex.toString()) || 0

                    const isStacked = actualStackedIndices.has(indexY)

                    if (isStacked) {
                      const unit = y.unit || "default"
                      const stackKey = `${rowIndex}-${unit}`

                      if (processedStackedRows.has(stackKey)) {
                        return
                      }
                      processedStackedRows.add(stackKey)

                      let groupIndices: number[] = []
                      stackedGroups.forEach((indices) => {
                        if (indices.includes(indexY)) {
                          groupIndices = indices
                        }
                      })

                      const stackPx =
                        (axisXDomain(rowIndex.toString()) || 0) +
                        axisYLeft[indexY] +
                        axisYWidth[indexY] / 2

                      // 누적 막대는 합 없이 조각마다 자기 값을 조각 안에 — 조각이 낮으면 생략 (2026-10-01 요청: 합산 표시 안 함)
                      let cumulative = 0
                      groupIndices.forEach((idx) => {
                        const segment = r.data[idx] || 0
                        if (segment <= 0) {
                          return
                        }
                        const top = axisYDomain[indexY](cumulative + segment) || 0
                        const bottom = axisYDomain[indexY](cumulative) || 0
                        cumulative += segment
                        if (bottom - top < minInsideHeight) {
                          return
                        }
                        addValueText(
                          svgCharts[indexChart],
                          stackPx,
                          (top + bottom) / 2 + style.fontSizeValue * 0.35,
                          segment,
                          chartMultiple.axisY[idx].color,
                          style,
                          decimalPointForExportFile,
                          axisLeftWidth,
                          chartMultiple.axisY[idx].unit,
                        )
                      })
                    } else {
                      let py =
                        (axisYDomain[indexY](value) || 0) -
                        style.fontSize * style.space.valueLineHeightRatio -
                        style.fontSize * style.space.betweenValueAndChartRatio

                      if (
                        y.chartType === CHART_TYPE.BAR ||
                        y.chartType === CHART_TYPE.STACKED_BAR
                      ) {
                        px += axisYLeft[indexY] + axisYWidth[indexY] / 2
                        py = avoidOverlap(px, py, value)
                      } else {
                        // 선·점은 항목 가운데 — 막대 값과 같은 자리면 한 줄 위로 (2026-10-01 보고: 선·막대 값 겹침)
                        px += widthOfXAxisItem / 2
                        py = avoidOverlap(px, py, value)
                      }

                      addValueText(
                        svgCharts[indexChart],
                        px,
                        py,
                        value,
                        y.color,
                        style,
                        decimalPointForExportFile,
                        axisLeftWidth,
                        y.unit,
                      )
                    }
                  }
                })
              }
            })
          }
          break

        // 막대 안(위쪽)에 값 — 막대가 낮으면 위에, 누적 막대는 조각 가운데, 선·점은 옆에 (PDF 빌더 "In chart", #185)
        case VALUE_POSITION_TYPE.IN_BAR:
          {
            const processedStackedRows = new Set<string>()
            const minInsideHeight = style.fontSizeValue * 1.8

            chartMultiple.axisY.forEach((y, indexY) => {
              if (!y.flagShow) {
                return
              }
              chart.rows.forEach((r, rowIndex) => {
                if (Number.isNaN(r.data[indexY])) {
                  return
                }
                const value = r.data[indexY] || 0
                const isStacked = actualStackedIndices.has(indexY)
                if (isStacked) {
                  const unit = y.unit || "default"
                  const stackKey = `${rowIndex}-${unit}`
                  if (processedStackedRows.has(stackKey)) {
                    return
                  }
                  processedStackedRows.add(stackKey)
                  let groupIndices: number[] = []
                  stackedGroups.forEach((indices) => {
                    if (indices.includes(indexY)) {
                      groupIndices = indices
                    }
                  })
                  const stackPx =
                    (axisXDomain(rowIndex.toString()) || 0) +
                    axisYLeft[indexY] +
                    axisYWidth[indexY] / 2
                  let cumulative = 0
                  groupIndices.forEach((idx) => {
                    const segment = r.data[idx] || 0
                    if (segment <= 0) {
                      return
                    }
                    const top = axisYDomain[indexY](cumulative + segment) || 0
                    const bottom = axisYDomain[indexY](cumulative) || 0
                    cumulative += segment
                    if (bottom - top < minInsideHeight) {
                      return
                    }
                    addValueText(
                      svgCharts[indexChart],
                      stackPx,
                      (top + bottom) / 2 + style.fontSizeValue * 0.35,
                      segment,
                      chartMultiple.axisY[idx].color,
                      style,
                      decimalPointForExportFile,
                      axisLeftWidth,
                      chartMultiple.axisY[idx].unit,
                    )
                  })

                  return
                }
                let px = axisXDomain(rowIndex.toString()) || 0
                let py: number
                if (y.chartType === CHART_TYPE.BAR || y.chartType === CHART_TYPE.STACKED_BAR) {
                  px += axisYLeft[indexY] + axisYWidth[indexY] / 2
                  const top = axisYDomain[indexY](value) || 0
                  const bottom = axisYDomain[indexY](0) || 0
                  py =
                    bottom - top >= minInsideHeight
                      ? top + style.fontSizeValue * 1.2
                      : top -
                        style.fontSize * style.space.valueLineHeightRatio -
                        style.fontSize * style.space.betweenValueAndChartRatio
                } else {
                  px += widthOfXAxisItem / 2
                  py =
                    (axisYDomain[indexY](value) || 0) -
                    style.fontSize * style.space.valueLineHeightRatio -
                    style.fontSize * style.space.betweenValueAndChartRatio
                }
                addValueText(
                  svgCharts[indexChart],
                  px,
                  py,
                  value,
                  y.color,
                  style,
                  decimalPointForExportFile,
                  axisLeftWidth,
                  y.unit,
                )
              })
            })
          }
          break

        case VALUE_POSITION_TYPE.IN_CHART_EACH_POSITION_MIN_MAX:
          {
            chartMultiple.axisY.forEach((y, indexY) => {
              if (y.flagShow) {
                let valueMax = 0
                let valueMin = 0
                let indexMax = 0
                let indexMin = 0

                chart.rows.forEach((r, index) => {
                  if (!Number.isNaN(r.data[indexY])) {
                    const value = r.data[indexY] || 0
                    if (index === 0) {
                      valueMax = value
                      valueMin = value
                    }
                    if (valueMax < value) {
                      valueMax = value
                      indexMax = index
                    }
                    if (valueMin > value) {
                      valueMin = value
                      indexMin = index
                    }
                  }
                })

                chart.rows.forEach((r, index) => {
                  if (!Number.isNaN(r.data[indexY]) && (index === indexMax || index === indexMin)) {
                    const value = r.data[indexY] || 0
                    let px = axisXDomain(index.toString()) || 0
                    const py =
                      (axisYDomain[indexY](value) || 0) -
                      style.fontSize * style.space.valueLineHeightRatio -
                      style.fontSize * style.space.betweenValueAndChartRatio

                    if (y.chartType === CHART_TYPE.BAR) {
                      px += axisYLeft[indexY] + axisYWidth[indexY] / 2
                    } else {
                      px += widthOfXAxisItem / 2
                    }

                    addValueText(
                      svgCharts[indexChart],
                      px,
                      py,
                      value || 0,
                      y.color,
                      style,
                      decimalPointForExportFile,
                      axisLeftWidth,
                      y.unit,
                    )
                  }
                })
              }
            })
          }
          break
      }
    }
  })

  let stackedGroupIndices: number[] = []
  stackedGroups.forEach((indices) => {
    if (indices.length > 1) {
      stackedGroupIndices = indices
    }
  })

  const hasStackedBars = stackedGroupIndices.length > 0

  if (chartMultiple.axisY.length > 0) {
    let leftAxisIndex = 0
    let showStackedOnLeft = false

    if (hasStackedBars && chartMultiple.axisY.length === 2) {
      leftAxisIndex = stackedGroupIndices[0]
      showStackedOnLeft = true
    } else if (
      hasStackedBars &&
      stackedGroupIndices.length === 2 &&
      chartMultiple.axisY.length === 3
    ) {
      for (let i = 0; i < chartMultiple.axisY.length; i++) {
        if (!stackedGroupIndices.includes(i)) {
          leftAxisIndex = i
          showStackedOnLeft = false
          break
        }
      }
    } else if (hasStackedBars && stackedGroupIndices.length === 3) {
      leftAxisIndex = stackedGroupIndices[0]
      showStackedOnLeft = true
    } else {
      leftAxisIndex = 0
      showStackedOnLeft = false
    }

    if (!flagEmpty) {
      svgYAxis[0]
        .append("g")
        .attr("transform", `translate(${style.rectAreaYAxis[0].right}, 0)`)
        .attr("stroke-width", style.gridStrokeWidth)
        .call(
          d3
            .axisLeft(axisYDomain[leftAxisIndex])
            .ticks(axisYTicks[leftAxisIndex])
            .tickFormat((y) => getLocaleString(Number(y), axisYTickDecimalPoint[leftAxisIndex])),
        )
        .call((g) => g.select(".domain").remove())
        .call((g) => {
          if (!style.flagShowTickStroke) {
            g.selectAll(".tick line").remove()
          }
        })
        .call((g) => {
          g.selectAll(".tick text")
            .style("fill", style.colorAxisYLabel)
            .style("font-family", style.fontFamily)
            .style("font-weight", style.fontWeightNoData)
            .style("font-size", style.fontSizeNoData)
        })
    }

    let yAxisLabel = ""

    if (showStackedOnLeft) {
      yAxisLabel = t ? t("stackedMetric") : "Stacked Metric"
      yAxisLabel += ` (${convertUnitSquare(chartMultiple.axisY[stackedGroupIndices[0]].unit || "")})`
    } else {
      yAxisLabel =
        (translateKey && translateKey(chartMultiple.axisY[leftAxisIndex].key || "")) || ""

      if (chartMultiple.axisY[leftAxisIndex].unit) {
        yAxisLabel += ` (${convertUnitSquare(chartMultiple.axisY[leftAxisIndex].unit || "")})`
      }
    }

    svgYAxis[0]
      .append("text")
      .attr("y", style.margin.left + style.space.yAxisLabelWidth - style.space.yAxisTickAndSpace)
      .attr("x", style.margin.top - style.rectAreaYAxis[0].height / 2)
      .attr("text-anchor", "middle")
      .attr("transform", "rotate(-90)")
      .style("fill", style.colorAxisYLabel)
      .style("font-family", style.fontFamily)
      .style("font-size", style.fontSize)
      .text(yAxisLabel)
  }

  if (
    style.rectAreaYAxis[1] &&
    !(hasStackedBars && stackedGroupIndices.length === 3) &&
    !(hasStackedBars && stackedGroupIndices.length === 2 && chartMultiple.axisY.length === 2)
  ) {
    let leftOfRightYAxis = style.rectAreaYAxis[1].left

    if (hasStackedBars && stackedGroupIndices.length === 2 && chartMultiple.axisY.length === 3) {
      if (!flagEmpty) {
        svgYAxis[1]
          .append("g")
          .attr("transform", `translate(${leftOfRightYAxis}, 0)`)
          .attr("stroke-width", style.gridStrokeWidth)
          .call(
            d3
              .axisRight(axisYDomain[stackedGroupIndices[0]])
              .ticks(axisYTicks[stackedGroupIndices[0]])
              .tickFormat((y) =>
                getLocaleString(Number(y), axisYTickDecimalPoint[stackedGroupIndices[0]]),
              ),
          )
          .call((g) => g.select(".domain").remove())
          .call((g) => {
            if (!style.flagShowTickStroke) {
              g.selectAll(".tick line").remove()
            }
          })
          .call((g) => {
            g.selectAll(".tick text")
              .style("fill", style.colorAxisYLabel)
              .style("font-family", style.fontFamily)
              .style("font-weight", style.fontWeightNoData)
              .style("font-size", style.fontSizeNoData)
          })
      }

      let yAxisLabel = t ? t("stackedMetric") : "Stacked Metric"
      yAxisLabel += ` (${convertUnitSquare(chartMultiple.axisY[stackedGroupIndices[0]].unit || "")})`

      const rightAxisTickWidth = axisYWidthForTickValue[stackedGroupIndices[0]] || 0
      const rightAxisYPosition = -(
        leftOfRightYAxis +
        rightAxisTickWidth +
        style.space.yAxisTickAndSpace +
        style.space.yAxisLabelWidth / 2
      )

      svgYAxis[1]
        .append("text")
        .attr("text-anchor", "middle")
        .attr("transform", "rotate(90)")
        .attr("y", rightAxisYPosition)
        .attr("x", style.rectAreaYAxis[1].top + style.rectAreaYAxis[1].height / 2)
        .style("fill", style.colorAxisYLabel)
        .style("font-family", style.fontFamily)
        .style("font-size", style.fontSize)
        .text(yAxisLabel)
    } else {
      for (let i = 1; i < chartMultiple.axisY.length; i++) {
        if (!flagEmpty) {
          svgYAxis[1]
            .append("g")
            .attr("transform", `translate(${leftOfRightYAxis}, 0)`)
            .attr("stroke-width", style.gridStrokeWidth)
            .call(
              d3
                .axisRight(axisYDomain[i])
                .ticks(axisYTicks[i])
                .tickFormat((y) => getLocaleString(Number(y), axisYTickDecimalPoint[i])),
            )
            .call((g) => g.select(".domain").remove())
            .call((g) => {
              if (!style.flagShowTickStroke) {
                g.selectAll(".tick line").remove()
              }
            })
            .call((g) => {
              g.selectAll(".tick text")
                .style("fill", style.colorAxisYLabel)
                .style("font-family", style.fontFamily)
                .style("font-weight", style.fontWeightNoData)
                .style("font-size", style.fontSizeNoData)
            })
        }

        leftOfRightYAxis +=
          style.space.yAxisLabelWidth +
          style.space.yAxisTickAndSpace +
          axisYWidthForTickValue[i] +
          style.space.betweenRightYAxis

        let yAxisLabel = (translateKey && translateKey(chartMultiple.axisY[i].key || "")) || ""

        if (chartMultiple.axisY[i].unit) {
          yAxisLabel += ` (${convertUnitSquare(chartMultiple.axisY[i].unit || "")})`
        }
        svgYAxis[1]
          .append("text")
          .attr("text-anchor", "middle")
          .attr("transform", "rotate(90)")
          .attr("y", -leftOfRightYAxis + style.space.yAxisLabelWidth)
          .attr("x", style.rectAreaYAxis[1].top + style.rectAreaYAxis[1].height / 2)
          .style("fill", style.colorAxisYLabel)
          .style("font-family", style.fontFamily)
          .style("font-size", style.fontSize)
          .text(yAxisLabel)
      }
    }
  }

  return {
    svgCharts: [...svgCharts.map((svg) => svg.node() ?? null)],
    svgYAxisLeft: svgYAxis[0]?.node() ?? null,
    svgYAxisRight: svgYAxis[1]?.node() ?? null,
  }
}

function getDistanceBetweenCoordinates(p1: number[], p2: number[]) {
  return Math.sqrt((p1[0] - p2[0]) ** 2 + (p1[1] - p2[1]) ** 2)
}

function addValueText(
  svg: d3.Selection<SVGSVGElement, undefined, null, undefined>,
  x: number,
  y: number,
  value: number,
  color: string,
  style: ChartStyle,
  decimalPoint: number = 1,
  axisLeftWidth: number = 0,
  unit: string | null = null,
) {
  // 축 단위가 정수 단위(bpm)면 내보내기 자릿수 선택을 무시하고 정수로 (#169)
  const valueString = getLocaleString(Number(value), resolveDecimalPoint(unit, decimalPoint))

  let colorText = style.fontColorValue
  switch (style.valueOrnamentType) {
    case VALUE_ORNAMENT_TYPE.USE_CHART_COLOR:
      {
        colorText = color
      }
      break

    case VALUE_ORNAMENT_TYPE.USE_CHART_COLOR_DARK:
      {
        colorText = d3.color(color)?.darker(0.5).toString() || color
      }
      break
  }

  svg
    .append("text")
    .attr("y", y)
    .attr("x", x)
    .attr("text-anchor", "middle")
    .style("fill", colorText)
    .style("font-family", style.fontFamily)
    .style("font-size", style.fontSizeValue)
    .style("font-weight", style.fontWeightValue)
    .style("paint-order", "stroke")
    .style("stroke-width", `${style.fontStrokeWidthValue}px`)
    .style("stroke-linecap", "butt")
    .style("stroke-linejoin", "miter")
    .style("stroke-linejoin", "round")
    .style("stroke", colors.neutral.white)
    // .attr("transform", style.isPreview ? `translate(${axisLeftWidth}, 0)` : null)
    .text(valueString)
}
